import { ApiError } from "@anna-money/anna-web-lib/api";
import { makeAutoObservable } from "mobx";

import type { AnalyticsManager } from "services/analytics/analyticsManager";
import { type SumsubApplication } from "services/sumsub/sumsubTypes";
import { type SumsubClient } from "./sumsubClient";

enum DocumentType {
  PASSPORT = "Passport",
  ID_CARD = "Identity card",
  DRIVERS = "Driving licence",
}
export class SumsubApplicationStore {
  private readonly _sumsubClient: SumsubClient;
  private _sumsubApplication?: SumsubApplication = undefined;
  private _isPassedVerifications: boolean | undefined;

  constructor(
    sumsubClient: SumsubClient,
    private readonly _analyticsManager: AnalyticsManager,
  ) {
    this._sumsubClient = sumsubClient;

    makeAutoObservable(this);
  }

  get sumsubApplication(): SumsubApplication | undefined {
    return this._sumsubApplication;
  }

  set sumsubApplication(value: SumsubApplication | undefined) {
    this._sumsubApplication = value;
  }

  get documentTypeValue(): string {
    const docType = this._sumsubApplication?.documentType as keyof typeof DocumentType;
    return DocumentType[docType] || DocumentType.DRIVERS;
  }

  get isPassedVerifications(): boolean {
    if (this._isPassedVerifications !== undefined) {
      return this._isPassedVerifications;
    }
    this._isPassedVerifications = this._sumsubApplication?.isPassedVerifications ?? false;
    return this._isPassedVerifications;
  }

  set isPassedVerifications(value: boolean) {
    this._isPassedVerifications = value;
  }

  get hasAllDocsDetails(): boolean {
    return this._sumsubApplication?.hasAllDocsDetails ?? false;
  }

  get needsNameExtension(): boolean {
    return this._sumsubApplication?.needNameExtension ?? false;
  }

  async init(): Promise<void> {
    await this.getSumsubApplication();
  }

  async getSumsubApplication(): Promise<void> {
    try {
      this.sumsubApplication = await this._sumsubClient.getSumsubApplication();
      this._analyticsManager.event("sumsub.application-status", {
        ...this.sumsubApplication,
        isPassedVerifications: this.isPassedVerifications,
      });
    } catch (e) {
      if (e instanceof ApiError && e.code !== 1040) {
        throw e;
      }
    }
  }
}
